const API_URL = `${process.env.PUBLIC_URL || ''}/api/index.php`;

async function apiRequest(payload) {
  try {
    const res = await fetch(API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const json = await res.json();
    if (!res.ok && !json.error) {
      return { data: null, error: { message: `HTTP ${res.status}` }, count: null };
    }
    return {
      data: json.data ?? null,
      error: json.error ?? null,
      count: json.count ?? null,
    };
  } catch (err) {
    return { data: null, error: { message: err.message || 'Network error' }, count: null };
  }
}

class QueryBuilder {
  constructor(table) {
    this.table = table;
    this._action = 'select';
    this._columns = '*';
    this._filters = [];
    this._order = [];
    this._limit = null;
    this._offset = null;
    this._countExact = false;
    this._head = false;
    this._single = false;
    this._rows = null;
    this._values = null;
    this._returning = true;
  }

  select(columns = '*', opts = {}) {
    // After insert/update/upsert, .select() means "return rows"
    if (['insert', 'update', 'upsert', 'delete'].includes(this._action)) {
      this._returning = true;
      if (columns && columns !== '*') this._columns = columns;
      return this;
    }
    this._action = 'select';
    this._columns = columns || '*';
    if (opts.count === 'exact') this._countExact = true;
    if (opts.head) this._head = true;
    return this;
  }

  insert(rows) {
    this._action = 'insert';
    this._rows = Array.isArray(rows) ? rows : [rows];
    this._returning = false;
    return this;
  }

  upsert(rows) {
    this._action = 'upsert';
    this._rows = Array.isArray(rows) ? rows : [rows];
    this._returning = false;
    return this;
  }

  update(values) {
    this._action = 'update';
    this._values = values;
    this._returning = false;
    return this;
  }

  delete() {
    this._action = 'delete';
    return this;
  }

  eq(column, value) {
    this._filters.push(['eq', column, value]);
    return this;
  }

  neq(column, value) {
    this._filters.push(['neq', column, value]);
    return this;
  }

  gt(column, value) {
    this._filters.push(['gt', column, value]);
    return this;
  }

  gte(column, value) {
    this._filters.push(['gte', column, value]);
    return this;
  }

  lt(column, value) {
    this._filters.push(['lt', column, value]);
    return this;
  }

  lte(column, value) {
    this._filters.push(['lte', column, value]);
    return this;
  }

  in(column, values) {
    this._filters.push(['in', column, values]);
    return this;
  }

  is(column, value) {
    this._filters.push(['is', column, value]);
    return this;
  }

  order(column, opts = {}) {
    const ascending = opts.ascending !== false;
    this._order.push([column, ascending]);
    return this;
  }

  limit(n) {
    this._limit = n;
    return this;
  }

  range(from, to) {
    this._offset = from;
    this._limit = to - from + 1;
    return this;
  }

  single() {
    this._single = true;
    return this;
  }

  maybeSingle() {
    this._single = true;
    return this;
  }

  then(resolve, reject) {
    return this.execute().then(resolve, reject);
  }

  async execute() {
    const payload = {
      action: this._action,
      table: this.table,
      columns: this._columns,
      filters: this._filters,
      order: this._order,
      limit: this._limit,
      offset: this._offset,
      countExact: this._countExact,
      head: this._head,
      single: this._single,
      rows: this._rows,
      values: this._values,
      returning: this._returning,
    };
    return apiRequest(payload);
  }
}

export const mysql = {
  from(table) {
    return new QueryBuilder(table);
  },
  rpc(fn, args = {}) {
    return apiRequest({ action: 'rpc', fn, args });
  },
  storage: {
    from() {
      return {
        async upload() {
          return { data: null, error: { message: 'File storage not configured for MySQL backend' } };
        },
        getPublicUrl() {
          return { data: { publicUrl: '' } };
        },
        async remove() {
          return { data: null, error: { message: 'File storage not configured for MySQL backend' } };
        },
      };
    },
  },
};

export default mysql;
